/** Cart delivery estimate and shipping conditions, refreshed with the cart. */
(() => {
  if (customElements.get('ft-cart-shipping')) return;

  class FtCartShipping extends HTMLElement {
    connectedCallback() {
      this.estimate = this.querySelector('[data-delivery-estimate]');
      this.free = this.querySelector('[data-free-shipping]');
      this.drawer = this.closest('cart-drawer');
      this.beforeOpen = () => this.refresh();
      this.drawer?.addEventListener('on:cart-drawer:before-open', this.beforeOpen);
      this.free.hidden = this.dataset.zip ? true : !this.qualifiesForFreeFallback();
      this.renderEstimate();
      if (this.drawer?.hasAttribute('open')) this.refresh();
    }

    disconnectedCallback() {
      this.drawer?.removeEventListener('on:cart-drawer:before-open', this.beforeOpen);
      this.controller?.abort();
    }

    today() {
      const parts = new Intl.DateTimeFormat('en', {
        timeZone: this.dataset.timeZone,
        year: 'numeric', month: 'numeric', day: 'numeric'
      }).formatToParts(new Date());
      const part = (type) => Number(parts.find((item) => item.type === type).value);
      return new Date(Date.UTC(part('year'), part('month') - 1, part('day'), 12));
    }

    businessDate(start, days) {
      const date = new Date(start);
      for (let remaining = days; remaining > 0;) {
        date.setUTCDate(date.getUTCDate() + 1);
        if (date.getUTCDay() !== 0 && date.getUTCDay() !== 6) remaining -= 1;
      }
      return date;
    }

    qualifiesForFreeFallback() {
      const subtotal = Number(this.dataset.subtotal);
      if (!Number.isFinite(subtotal) || subtotal < 0) return false;
      return (this.dataset.freeRules || '').split(/\r?\n/).some((line) => {
        const match = line.trim().match(/^([A-Z]{2}):([A-Z]{3}|\*):(\d+)$/);
        if (!match || match[1] !== this.dataset.country) return false;
        const minimum = Number(match[3]);
        const currencyMatches = match[2] === this.dataset.currency || (match[2] === '*' && minimum === 0);
        return currencyMatches && subtotal >= minimum;
      });
    }

    policyRange() {
      if (this.dataset.country !== 'DE') return null;
      const match = this.dataset.policy.match(
        /(?:Lieferzeit|delivery\s+time|shipping\s+time)[^.\n]{0,160}?(\d+)\s*(?:-|–|bis|to)\s*(\d+)\s*(?:Werk|business\s+|working\s+)?(?:Tage|days)/i
      );
      if (!match) return null;
      const minimum = Number(match[1]);
      const maximum = Number(match[2]);
      if (minimum < 1 || maximum < minimum || maximum > 30) return null;
      const today = this.today();
      return [this.businessDate(today, minimum), this.businessDate(today, maximum)];
    }

    parseDate(value) {
      if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}(?:T|$)/.test(value)) return null;
      const date = new Date(`${value.slice(0, 10)}T12:00:00Z`);
      return Number.isNaN(date.getTime()) ? null : date;
    }

    rateRange(rate) {
      if (Array.isArray(rate?.delivery_range) && rate.delivery_range.length === 2) {
        const dates = rate.delivery_range.map((date) => this.parseDate(date));
        if (dates.every(Boolean) && dates[0] <= dates[1]) return dates;
      }
      const date = this.parseDate(rate?.delivery_date);
      return date ? [date, date] : null;
    }

    formatDate(date) {
      const formatter = new Intl.DateTimeFormat(this.dataset.locale || 'de', {
        timeZone: 'UTC', weekday: 'short', day: 'numeric', month: 'numeric'
      });
      if (this.dataset.locale?.startsWith('de')) {
        const parts = formatter.formatToParts(date);
        const part = (type) => parts.find((item) => item.type === type)?.value;
        return `${part('weekday')} ${part('day')}.${part('month')}.`;
      }
      return formatter.format(date);
    }

    renderEstimate(rate) {
      let dates = this.rateRange(rate) || this.policyRange();
      if (this.dataset.readyToShip !== 'true') dates = null;
      this.estimate.hidden = !dates;
      if (dates) {
        const [earliest, latest] = dates.map((date) => this.formatDate(date));
        this.estimate.textContent = earliest === latest
          ? this.dataset.dateText.replace('__date__', earliest)
          : this.dataset.rangeText.replace('__earliest__', earliest).replace('__latest__', latest);
      }
      this.hidden = this.estimate.hidden && this.free.hidden;
    }

    async refresh() {
      if (this.loading || (this.loadedAt && Date.now() - this.loadedAt < 60000)) return;
      this.free.hidden = this.dataset.zip ? true : !this.qualifiesForFreeFallback();
      this.renderEstimate();
      if (!this.dataset.country || !this.dataset.zip) return;
      this.loading = true;
      this.controller = new AbortController();
      const { signal } = this.controller;
      const timeout = setTimeout(() => this.controller.abort(), 10000);
      const params = new URLSearchParams({
        'shipping_address[country]': this.dataset.country,
        'shipping_address[zip]': this.dataset.zip || '',
        'shipping_address[province]': this.dataset.province || ''
      });
      const root = window.Shopify?.routes?.root || '/';
      const options = { credentials: 'same-origin', signal, headers: { Accept: 'application/json' } };

      try {
        const prepared = await fetch(`${root}cart/prepare_shipping_rates.json?${params}`, {
          ...options, method: 'POST'
        });
        if (!prepared.ok) return;
        for (let attempt = 0; attempt < 8 && !signal.aborted; attempt += 1) {
          const response = await fetch(`${root}cart/async_shipping_rates.json?${params}`, options);
          if (!response.ok) return;
          const result = await response.json();
          if (result !== null) {
            if (!this.isConnected || signal.aborted) return;
            const rates = Array.isArray(result.shipping_rates) ? result.shipping_rates.filter((rate) => (
              rate.price !== null && rate.price !== undefined && String(rate.price).trim() !== ''
              && Number.isFinite(Number(rate.price)) && Number(rate.price) >= 0
            )) : [];
            rates.sort((left, right) => Number(left.price) - Number(right.price));
            this.free.hidden = !rates.some((rate) => Number(rate.price) === 0);
            this.renderEstimate(rates[0]);
            this.loadedAt = Date.now();
            return;
          }
          await new Promise((resolve) => {
            const finish = () => { clearTimeout(timer); signal.removeEventListener('abort', finish); resolve(); };
            const timer = setTimeout(finish, Math.min(250 * (attempt + 1), 1000));
            signal.addEventListener('abort', finish, { once: true });
            if (signal.aborted) finish();
          });
        }
      } catch (error) {
        // An incomplete destination or an unavailable rate must never advertise free shipping.
      } finally {
        clearTimeout(timeout);
        this.loading = false;
      }
    }
  }

  customElements.define('ft-cart-shipping', FtCartShipping);
})();
